/**
 * Supabase Installations API Service
 *
 * Handles all installation/calendar operations.
 */

import { supabase } from './client';
import type { Installation, InstallationInsert, InstallationUpdate } from './database.types';

// Installation status type
export type InstallationStatus = 'scheduled' | 'in_progress' | 'completed' | 'cancelled';

// Crew type
export type CrewType = 'crew_a' | 'crew_b' | 'crew_c' | 'crew_d';

// Frontend Installation type (camelCase)
export interface InstallationData {
  id: string;
  createdAt: string;
  updatedAt: string;
  leadId: string | null;
  title: string;
  clientName: string;
  clientAddress: string;
  distanceMiles: number | null;
  driveTime: string | null;
  crew: CrewType | null;
  installers: CrewType[] | null;
  scheduledDate: string;
  startTime: string | null;
  endTime: string | null;
  allDay: boolean;
  status: InstallationStatus;
  notes: string | null;
  userId: string | null;
}

// Map database installation to frontend format
const mapInstallationToData = (installation: Installation): InstallationData => ({
  id: installation.id,
  createdAt: installation.created_at,
  updatedAt: installation.updated_at,
  leadId: installation.lead_id,
  title: installation.title,
  clientName: installation.client_name,
  clientAddress: installation.client_address,
  distanceMiles: installation.distance_miles,
  driveTime: installation.drive_time,
  crew: installation.crew,
  installers: installation.installers || null,
  scheduledDate: installation.scheduled_date,
  startTime: installation.start_time,
  endTime: installation.end_time,
  allDay: installation.all_day,
  status: installation.status,
  notes: installation.notes,
  userId: installation.user_id,
});

// Flag to track if installers column exists in the database
let installersColumnExists: boolean | null = null;

// Check if installers column exists
const checkInstallersColumn = async (): Promise<boolean> => {
  if (installersColumnExists !== null) return installersColumnExists;

  try {
    // Try a query that includes installers column
    const { error } = await supabase
      .from('installations')
      .select('installers')
      .limit(1);

    installersColumnExists = !error || !error.message.includes('installers');
    return installersColumnExists;
  } catch {
    installersColumnExists = false;
    return false;
  }
};

// Map frontend installation to database format
const mapDataToInstallation = (data: Partial<InstallationData>, includeInstallers: boolean = true): Partial<InstallationInsert> => {
  const mapped: Partial<InstallationInsert> = {};

  if (data.leadId !== undefined) mapped.lead_id = data.leadId;
  if (data.title !== undefined) mapped.title = data.title;
  if (data.clientName !== undefined) mapped.client_name = data.clientName;
  if (data.clientAddress !== undefined) mapped.client_address = data.clientAddress;
  if (data.distanceMiles !== undefined) mapped.distance_miles = data.distanceMiles;
  if (data.driveTime !== undefined) mapped.drive_time = data.driveTime;
  if (data.crew !== undefined) mapped.crew = data.crew;
  // Only include installers if the column exists in the database
  if (includeInstallers && data.installers !== undefined) mapped.installers = data.installers;
  if (data.scheduledDate !== undefined) mapped.scheduled_date = data.scheduledDate;
  if (data.startTime !== undefined) mapped.start_time = data.startTime;
  if (data.endTime !== undefined) mapped.end_time = data.endTime;
  if (data.allDay !== undefined) mapped.all_day = data.allDay;
  if (data.status !== undefined) mapped.status = data.status;
  if (data.notes !== undefined) mapped.notes = data.notes;
  if (data.userId !== undefined) mapped.user_id = data.userId;

  return mapped;
};

export interface GetInstallationsParams {
  leadId?: string;
  crew?: CrewType;
  status?: InstallationStatus;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
}

export interface GetInstallationsResponse {
  data: InstallationData[];
  count: number;
}

class InstallationsApi {
  /**
   * Get all installations with optional filters
   */
  async getAll(params: GetInstallationsParams = {}): Promise<GetInstallationsResponse> {
    const pageSize = params.pageSize || 100;
    const page = params.page || 1;
    const offset = (page - 1) * pageSize;

    let query = supabase
      .from('installations')
      .select('*', { count: 'exact' });

    // Apply filters
    if (params.leadId) {
      query = query.eq('lead_id', params.leadId);
    }

    if (params.crew) {
      query = query.eq('crew', params.crew);
    }

    if (params.status) {
      query = query.eq('status', params.status);
    }

    if (params.startDate) {
      query = query.gte('scheduled_date', params.startDate);
    }

    if (params.endDate) {
      query = query.lte('scheduled_date', params.endDate);
    }

    // Apply sorting (by scheduled date)
    query = query.order('scheduled_date', { ascending: true });

    // Apply pagination
    query = query.range(offset, offset + pageSize - 1);

    const { data, error, count } = await query;

    if (error) {
      // console.error('Failed to fetch installations:', error);
      throw error;
    }

    return {
      data: (data || []).map(mapInstallationToData),
      count: count || 0,
    };
  }

  /**
   * Get installations for a specific lead
   */
  async getByLead(leadId: string): Promise<InstallationData[]> {
    const { data, error } = await supabase
      .from('installations')
      .select('*')
      .eq('lead_id', leadId)
      .order('scheduled_date', { ascending: true });

    if (error) {
      // console.error('Failed to fetch installations by lead:', error);
      throw error;
    }

    return (data || []).map(mapInstallationToData);
  }

  /**
   * Get installations for a date range (calendar view)
   */
  async getByDateRange(startDate: string, endDate: string): Promise<InstallationData[]> {
    const { data, error } = await supabase
      .from('installations')
      .select('*')
      .gte('scheduled_date', startDate)
      .lte('scheduled_date', endDate)
      .order('scheduled_date', { ascending: true });

    if (error) {
      // console.error('Failed to fetch installations by date range:', error);
      throw error;
    }

    return (data || []).map(mapInstallationToData);
  }

  /**
   * Get installations by crew
   */
  async getByCrew(crew: CrewType): Promise<InstallationData[]> {
    const { data, error } = await supabase
      .from('installations')
      .select('*')
      .eq('crew', crew)
      .order('scheduled_date', { ascending: true });

    if (error) {
      // console.error('Failed to fetch installations by crew:', error);
      throw error;
    }

    return (data || []).map(mapInstallationToData);
  }

  /**
   * Get a single installation by ID
   */
  async getById(id: string): Promise<InstallationData | null> {
    const { data, error } = await supabase
      .from('installations')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      // console.error('Failed to fetch installation:', error);
      throw error;
    }

    return data ? mapInstallationToData(data) : null;
  }

  /**
   * Create a new installation
   */
  async create(installationData: Omit<InstallationData, 'id' | 'createdAt' | 'updatedAt'>): Promise<InstallationData> {
    // console.log('installationsApi.create called with:', {
    //   title: installationData.title,
    //   scheduledDate: installationData.scheduledDate,
    // });

    // Check if installers column exists before including it
    const includeInstallers = await checkInstallersColumn();
    const dbData = mapDataToInstallation(installationData, includeInstallers) as InstallationInsert;

    const { data, error } = await supabase
      .from('installations')
      .insert(dbData as any)
      .select()
      .single();

    if (error) {
      // console.error('Failed to create installation:', error);
      throw new Error(`Installation creation failed: ${error.message}`);
    }

    // console.log('Installation created successfully:', (data as Installation).id);
    return mapInstallationToData(data as Installation);
  }

  /**
   * Update an existing installation
   */
  async update(id: string, installationData: Partial<InstallationData>): Promise<InstallationData> {
    // Check if installers column exists before including it
    const includeInstallers = await checkInstallersColumn();
    const dbData = mapDataToInstallation(installationData, includeInstallers) as InstallationUpdate;

    const { data, error } = await (supabase
      .from('installations')
      .update(dbData as any)
      .eq('id', id)
      .select()
      .single() as any);

    if (error) {
      // console.error('Failed to update installation:', error);
      throw error;
    }

    return mapInstallationToData(data as Installation);
  }

  /**
   * Delete an installation
   */
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('installations')
      .delete()
      .eq('id', id);

    if (error) {
      // console.error('Failed to delete installation:', error);
      throw error;
    }
  }

  /**
   * Update installation status
   */
  async updateStatus(id: string, status: InstallationStatus): Promise<InstallationData> {
    return this.update(id, { status });
  }

  /**
   * Assign crew to installation
   */
  async assignCrew(id: string, crew: CrewType): Promise<InstallationData> {
    return this.update(id, { crew });
  }

  /**
   * Reschedule installation
   */
  async reschedule(
    id: string,
    scheduledDate: string,
    startTime?: string,
    endTime?: string
  ): Promise<InstallationData> {
    return this.update(id, {
      scheduledDate,
      startTime: startTime || null,
      endTime: endTime || null,
    });
  }

  /**
   * Get today's installations
   */
  async getToday(): Promise<InstallationData[]> {
    const today = new Date().toISOString().split('T')[0];
    return this.getByDateRange(today, today);
  }

  /**
   * Get upcoming installations (next 7 days)
   */
  async getUpcoming(days: number = 7): Promise<InstallationData[]> {
    const today = new Date();
    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() + days);

    return this.getByDateRange(
      today.toISOString().split('T')[0],
      endDate.toISOString().split('T')[0]
    );
  }
}

export const installationsApi = new InstallationsApi();

// Installation status labels
export const installationStatusLabels: Record<InstallationStatus, string> = {
  scheduled: 'Scheduled',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

// Installation status colors
export const installationStatusColors: Record<InstallationStatus, string> = {
  scheduled: '#3B82F6',
  in_progress: '#F59E0B',
  completed: '#10B981',
  cancelled: '#EF4444',
};

// Crew labels
export const crewLabels: Record<CrewType, string> = {
  crew_a: 'Crew A',
  crew_b: 'Crew B',
  crew_c: 'Crew C',
  crew_d: 'Crew D',
};

// Crew colors
export const crewColors: Record<CrewType, string> = {
  crew_a: '#3B82F6',
  crew_b: '#10B981',
  crew_c: '#F59E0B',
  crew_d: '#8B5CF6',
};
